import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { FilterSelect, HpButton, PageHeading, SearchField, Segmented, Toolbar, ToolbarSpacer } from '@hp/ui';
import { SEGMENT_LABELS, type Segment } from '@hp/domain';
import { useCan, useMe } from '../api/me';
import { trpc } from '../api/trpc';
import { isRunActive } from '../lib/runs';
import { ErrorAlert } from '../components/error-alert';
import { LoadMore } from '../components/ui/load-more';
import { Stat, StatStrip } from '../components/ui/stat';
import { DealTable } from '../features/segmentation/deal-table';
import { useSegmentationRuns } from '../features/segmentation/queries';
import { RunPanel } from '../features/segmentation/run-panel';

/** Segment tab values; "all" is labelled from `common`, the rest are the Pipedrive VALUES (not translated). */
const SEGMENT_TAB_VALUES = ['all', 'investicni', 'rezidencni', 'nezarazeno'] as const;

const STATUSES = ['open', 'won', 'lost'] as const;
type Status = (typeof STATUSES)[number];

export default function Segmentation() {
  const { t } = useTranslation(['segmentation', 'common']);
  const me = useMe();
  const can = useCan();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');

  const rawSegment = searchParams.get('segment');
  const segment: Segment | '' = SEGMENT_TAB_VALUES.some((v) => v === rawSegment && v !== 'all')
    ? (rawSegment as Segment)
    : '';
  const rawStatus = searchParams.get('status') ?? '';
  const status: Status | '' = STATUSES.some((x) => x === rawStatus) ? (rawStatus as Status) : '';
  const q = searchParams.get('q') ?? '';

  const runs = useSegmentationRuns();
  const active = runs.data?.some((r) => isRunActive(r.status)) ?? false;
  const stats = useQuery(trpc.segmentation.stats.queryOptions(undefined, { refetchInterval: active ? 5000 : false }));
  const deals = useInfiniteQuery(
    trpc.segmentation.deals.infiniteQueryOptions(
      { segment: segment || undefined, status: status || undefined, q: q || undefined },
      { getNextPageParam: (last) => last.next },
    ),
  );

  const items = useMemo(() => deals.data?.pages.flatMap((p) => p.items) ?? [], [deals.data]);
  const bysegment = (s: Segment) => stats.data?.segments.find((x) => x.segment === s);
  const segmentTabs = SEGMENT_TAB_VALUES.map((value) => ({
    value,
    label: value === 'all' ? t('common:misc.all') : SEGMENT_LABELS[value],
  }));

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  }

  const exportHref = useMemo(() => {
    const params = new URLSearchParams();
    if (segment) params.set('segment', segment);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const qs = params.toString();
    return `/api/segmentation/export.csv${qs ? `?${qs}` : ''}`;
  }, [segment, status, q]);

  return (
    <div className="bo-sections">
      <PageHeading
        title={t('title')}
        description={t('description')}
        actions={
          <span className="bo-row">
            <span className="bo-muted">{t('export.label')}</span>
            <HpButton variant="secondary" icon="download" onClick={() => window.open(exportHref, '_self')}>
              {t('export.deals')}
            </HpButton>
            <HpButton
              icon="download"
              onClick={() => window.open(exportHref.replace('export.csv', 'export-activities.csv'), '_self')}
            >
              {t('export.activities')}
            </HpButton>
            <HpButton
              icon="download"
              onClick={() => window.open(exportHref.replace('export.csv', 'export-notes.csv'), '_self')}
            >
              {t('export.notes')}
            </HpButton>
          </span>
        }
      />

      <RunPanel canRun={can('crm.run_jobs')} />

      <StatStrip>
        <Stat label={SEGMENT_LABELS.investicni} value={bysegment('investicni')?.n} />
        <Stat label={SEGMENT_LABELS.rezidencni} value={bysegment('rezidencni')?.n} />
        <Stat label={SEGMENT_LABELS.nezarazeno} value={bysegment('nezarazeno')?.n} />
        <Stat label={t('stats.aiScanned')} value={stats.data?.llmScanned} />
        <Stat label={t('stats.aiQueue')} value={stats.data?.llmPending} />
      </StatStrip>

      <section className="hp-card">
        <Toolbar className="bo-toolbar">
          <Segmented
            ariaLabel={t('filters.segmentAria')}
            value={segment || 'all'}
            options={segmentTabs}
            onChange={(v) => setParam('segment', v === 'all' ? '' : v)}
          />
          <FilterSelect
            value={status}
            onChange={(e) => setParam('status', e.target.value)}
            aria-label={t('filters.dealStatusAria')}
          >
            <option value="">{t('dealStatus.any')}</option>
            <option value="open">{t('dealStatus.open')}</option>
            <option value="won">{t('dealStatus.won')}</option>
            <option value="lost">{t('dealStatus.lost')}</option>
          </FilterSelect>
          <form
            className="bo-row"
            onSubmit={(e) => {
              e.preventDefault();
              setParam('q', search.trim());
            }}
          >
            <SearchField
              className="bo-search bo-search--toolbar"
              placeholder={t('filters.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t('filters.searchAria')}
            />
            <HpButton type="submit" className="bo-button--sm">
              {t('common:actions.search')}
            </HpButton>
          </form>
          <ToolbarSpacer />
          <span className="bo-muted bo-mono">
            {deals.hasNextPage ? t('loadedMore', { count: items.length }) : t('loaded', { count: items.length })}
          </span>
        </Toolbar>

        {deals.isError && <ErrorAlert error={deals.error} className="bo-alert--inset" />}
        {deals.isLoading ? (
          <p className="hp-card__body bo-muted">{t('common:actions.loading')}</p>
        ) : (
          <DealTable deals={items} pdDomain={me.data?.pipedriveCompanyDomain} />
        )}
        {deals.hasNextPage && (
          <div className="hp-card__body bo-row">
            <LoadMore
              hasNextPage={deals.hasNextPage}
              isFetchingNextPage={deals.isFetchingNextPage}
              onClick={() => void deals.fetchNextPage()}
            />
          </div>
        )}
      </section>
    </div>
  );
}
